import { Case, CaseOpenNextStep, NEXT_STEP_TYPES, NextStepType } from '../../types';
import { getCutoffDate, TimeRangeFilter } from '../../utils/timeRange';

export type SessionReportsStatusFilter = 'active' | 'others';

export interface SessionReportRecord {
  id: string;
  caseId: string;
  sessionNumber: number;
  mainTheme: string;
  personResponse: string;
  previousTaskCompleted: 'yes' | 'no' | 'partial' | null;
  previousTaskNotCompletedReason?: string;
  /** Whether progress was observed this session. Null on opening session / legacy. */
  progressObserved: 'yes' | 'no' | 'partial' | null;
  progressNoted: string;
  nextCommitments: 'yes' | 'no';
  nextCommitmentsDetails?: string;
  /** Structured next-step type; null/missing on legacy reports → treat as other. */
  nextStepType?: NextStepType | null;
  /** Optional scripture reference for the open step. */
  nextStepPassage?: string | null;
  noCommitmentsReason?: string;
  createdAt: Date;
  createdBy: string;
  createdByName: string;
}

export function parseNextStepType(value: unknown): NextStepType | null {
  return NEXT_STEP_TYPES.includes(value as NextStepType)
    ? (value as NextStepType)
    : null;
}

/**
 * Open step to verify at the next session; null if none / no description.
 *
 * `allSessionNumbers` is required because the step is labelled with the road index, and only the
 * case's full set of stored numbers reveals whether that case starts at 0 or at 1.
 */
export function getReportOpenNextStep(
  report: Pick<
    SessionReportRecord,
    | 'nextCommitments'
    | 'nextCommitmentsDetails'
    | 'nextStepType'
    | 'nextStepPassage'
    | 'sessionNumber'
  >,
  allSessionNumbers: number[]
): CaseOpenNextStep | null {
  if (report.nextCommitments !== 'yes') return null;
  const description = report.nextCommitmentsDetails?.trim() ?? '';
  if (!description) return null;
  return {
    type: report.nextStepType ?? 'other',
    description,
    passage: report.nextStepPassage?.trim() || null,
    fromSessionNumber: toRoadSessionNumber(report.sessionNumber, allSessionNumbers),
  };
}

/** Latest report with sessionNumber strictly below the current one. */
export function findPreviousSessionReport(
  reports: SessionReportRecord[],
  sessionNumber: number
): SessionReportRecord | null {
  const prior = reports
    .filter((r) => r.sessionNumber < sessionNumber)
    .sort((a, b) => b.sessionNumber - a.sessionNumber);
  return prior[0] ?? null;
}

export interface CaseReportSummary {
  case: Case;
  reportCount: number;
  lastReportDate: Date | null;
  reports: SessionReportRecord[];
}

export function parseSessionReportDoc(
  id: string,
  data: Record<string, unknown>
): SessionReportRecord {
  const createdAt = data.createdAt as { toDate: () => Date };
  return {
    id,
    caseId: data.caseId as string,
    sessionNumber: parseSessionNumber(data.sessionNumber),
    mainTheme: data.mainTheme as string,
    personResponse: data.personResponse as string,
    previousTaskCompleted:
      data.previousTaskCompleted === 'yes' ||
      data.previousTaskCompleted === 'no' ||
      data.previousTaskCompleted === 'partial'
        ? data.previousTaskCompleted
        : null,
    previousTaskNotCompletedReason: (data.previousTaskNotCompletedReason as string) || '',
    progressObserved:
      data.progressObserved === 'yes' ||
      data.progressObserved === 'no' ||
      data.progressObserved === 'partial'
        ? data.progressObserved
        : null,
    progressNoted: (data.progressNoted as string) || '',
    nextCommitments: data.nextCommitments as 'yes' | 'no',
    nextCommitmentsDetails: data.nextCommitmentsDetails as string | undefined,
    nextStepType: parseNextStepType(data.nextStepType),
    nextStepPassage:
      typeof data.nextStepPassage === 'string' && data.nextStepPassage.trim()
        ? data.nextStepPassage.trim()
        : null,
    noCommitmentsReason: data.noCommitmentsReason as string | undefined,
    createdAt: createdAt.toDate(),
    createdBy: data.createdBy as string,
    createdByName: data.createdByName as string,
  };
}

export function parseSessionNumber(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

/** Next stored session index. Empty case starts at 0 (matches drum de sesiuni). */
export function nextStoredSessionNumber(sessionNumbers: number[]): number {
  if (sessionNumbers.length === 0) return 0;
  return Math.max(...sessionNumbers) + 1;
}

/**
 * Road / UI index is 0-based. Legacy reports start at 1 — map them down so
 * labels match drum de sesiuni. Cases that already have session 0 stay as stored.
 */
export function toRoadSessionNumber(
  sessionNumber: number,
  allSessionNumbers: number[]
): number {
  if (allSessionNumbers.length === 0) return sessionNumber;
  if (allSessionNumbers.some((n) => n === 0)) return sessionNumber;
  return sessionNumber - 1;
}

/** First meeting on the road: no previous theme or observed progress yet. */
export function isOpeningSession(
  sessionNumber: number,
  allSessionNumbers: number[]
): boolean {
  return sessionNumber === 0 || toRoadSessionNumber(sessionNumber, allSessionNumbers) === 0;
}

export function buildCaseSummaries(
  cases: Case[],
  reportsByCaseId: Map<string, SessionReportRecord[]>
): CaseReportSummary[] {
  return cases
    .map((caseItem) => {
      const reports = [...(reportsByCaseId.get(caseItem.id) ?? [])].sort(
        (a, b) => b.sessionNumber - a.sessionNumber
      );
      const lastReportDate = reports.length > 0 ? reports[0].createdAt : null;
      return {
        case: caseItem,
        reportCount: reports.length,
        lastReportDate,
        reports,
      };
    })
    .sort((a, b) => {
      if (a.reportCount === 0 && b.reportCount === 0) {
        return a.case.title.localeCompare(b.case.title, 'ro');
      }
      if (a.reportCount === 0) return 1;
      if (b.reportCount === 0) return -1;
      const aTime = a.lastReportDate?.getTime() ?? 0;
      const bTime = b.lastReportDate?.getTime() ?? 0;
      return bTime - aTime;
    });
}

export interface SessionReportsFilterState {
  searchTerm: string;
  counselorFilter: string;
  timeRangeFilter: TimeRangeFilter;
  statusFilter: SessionReportsStatusFilter;
}

export function shouldUseAllTimeForDeepLink(lastReportDate: Date | null): boolean {
  if (!lastReportDate) return false;
  return lastReportDate < getCutoffDate('3months');
}

export function createEmptyCaseSummary(caseItem: Case): CaseReportSummary {
  return {
    case: caseItem,
    reportCount: 0,
    lastReportDate: null,
    reports: [],
  };
}

export function findCaseReportSummary(
  caseId: string,
  allSummaries: CaseReportSummary[],
  allCases: Case[]
): CaseReportSummary | null {
  const existing = allSummaries.find((s) => s.case.id === caseId);
  if (existing) return existing;

  const caseItem = allCases.find((c) => c.id === caseId);
  return caseItem ? createEmptyCaseSummary(caseItem) : null;
}

/** Pin a deep-linked case at the top when it would otherwise be hidden by filters.
 * Never pin a case that fails the Active/Altele status toggle. */
export function buildCaseListSummaries(
  filteredSummaries: CaseReportSummary[],
  selectedCaseId: string | null,
  allSummaries: CaseReportSummary[],
  allCases: Case[],
  statusFilter: SessionReportsStatusFilter
): CaseReportSummary[] {
  if (!selectedCaseId) return filteredSummaries;
  if (filteredSummaries.some((s) => s.case.id === selectedCaseId)) {
    return filteredSummaries;
  }

  const pinned = findCaseReportSummary(selectedCaseId, allSummaries, allCases);
  if (!pinned) return filteredSummaries;

  const matchesStatus =
    statusFilter === 'active'
      ? pinned.case.status === 'active'
      : pinned.case.status !== 'active';
  if (!matchesStatus) return filteredSummaries;

  return [pinned, ...filteredSummaries];
}

export function filterCaseSummaries(
  summaries: CaseReportSummary[],
  filters: SessionReportsFilterState
): CaseReportSummary[] {
  const term = filters.searchTerm.trim().toLowerCase();
  const cutoff = getCutoffDate(filters.timeRangeFilter);

  return summaries.filter((summary) => {
    const { case: caseItem, reports, lastReportDate } = summary;

    if (filters.statusFilter === 'active') {
      if (caseItem.status !== 'active') return false;
    } else if (caseItem.status === 'active') {
      return false;
    }

    if (lastReportDate && lastReportDate < cutoff) {
      return false;
    }

    if (filters.counselorFilter !== 'all') {
      const hasCounselorReport = reports.some((r) => r.createdBy === filters.counselorFilter);
      if (!hasCounselorReport) {
        return false;
      }
    }

    if (!term) return true;

    const matchesCase =
      caseItem.title.toLowerCase().includes(term) ||
      caseItem.counseledName.toLowerCase().includes(term) ||
      (caseItem.assignedCounselorName?.toLowerCase().includes(term) ?? false);

    const matchesReport = reports.some(
      (r) =>
        r.mainTheme.toLowerCase().includes(term) ||
        r.createdByName.toLowerCase().includes(term) ||
        r.personResponse.toLowerCase().includes(term)
    );

    return matchesCase || matchesReport;
  });
}

export function computeSessionReportMetrics(summaries: CaseReportSummary[]) {
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  let totalReports = 0;
  let reportsThisMonth = 0;

  summaries.forEach((summary) => {
    totalReports += summary.reportCount;
    summary.reports.forEach((report) => {
      if (report.createdAt >= monthStart) {
        reportsThisMonth += 1;
      }
    });
  });

  return {
    totalReports,
    casesWithReports: summaries.filter((s) => s.reportCount > 0).length,
    reportsThisMonth,
  };
}

export function formatReportDate(date: Date): string {
  return date.toLocaleDateString('ro-RO', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function getTaskCompletedLabel(
  value: 'yes' | 'no' | 'partial' | null | undefined
): string {
  if (value === 'yes') return 'Da';
  if (value === 'partial') return 'Parțial';
  if (value === 'no') return 'Nu';
  return '';
}

export interface CaseThemeTrailItem {
  sessionNumber: number;
  roadSession: number;
  theme: string;
}

export interface CasePassageTrailItem {
  sessionNumber: number;
  roadSession: number;
  passage: string;
  theme: string;
}

export interface CaseCounselingTrail {
  themes: CaseThemeTrailItem[];
  passages: CasePassageTrailItem[];
  /** True when older themes were omitted from the display window. */
  themesTruncated: boolean;
}

const THEME_TRAIL_LIMIT = 6;

/** Chronological theme + scripture trail derived from session reports (C1). */
export function buildCaseCounselingTrail(
  reports: SessionReportRecord[],
  options?: { themeLimit?: number }
): CaseCounselingTrail {
  const limit = options?.themeLimit ?? THEME_TRAIL_LIMIT;
  const allNumbers = reports.map((r) => r.sessionNumber);
  const chronological = [...reports].sort((a, b) => {
    if (a.sessionNumber !== b.sessionNumber) return a.sessionNumber - b.sessionNumber;
    return a.createdAt.getTime() - b.createdAt.getTime();
  });

  const themesAll = chronological
    .filter((r) => r.mainTheme?.trim())
    .map((r) => ({
      sessionNumber: r.sessionNumber,
      roadSession: toRoadSessionNumber(r.sessionNumber, allNumbers),
      theme: r.mainTheme.trim(),
    }));

  const themesTruncated = themesAll.length > limit;
  const themes = themesTruncated ? themesAll.slice(-limit) : themesAll;

  const passages = chronological
    .filter((r) => r.nextStepPassage?.trim())
    .map((r) => ({
      sessionNumber: r.sessionNumber,
      roadSession: toRoadSessionNumber(r.sessionNumber, allNumbers),
      passage: (r.nextStepPassage as string).trim(),
      theme: r.mainTheme?.trim() ?? '',
    }));

  return { themes, passages, themesTruncated };
}

export function formatThemeTrailLine(trail: CaseCounselingTrail): string {
  if (trail.themes.length === 0) return '';
  const body = trail.themes
    .map((item) => `#${item.roadSession} ${item.theme}`)
    .join(' → ');
  return trail.themesTruncated ? `… → ${body}` : body;
}

export function formatPassageTrailLine(trail: CaseCounselingTrail): string {
  if (trail.passages.length === 0) return '';
  return trail.passages
    .map((item) => `#${item.roadSession} ${item.passage}`)
    .join(' · ');
}
